import React, { useEffect, useMemo, useRef, useState } from 'react';
import StoryMedia from '../../stories/StoryMedia';
import PortalBackground from './PortalBackground';
import PortalWords from './PortalWords';
import PortalVenue from './PortalVenue';
import PortalEvent from './PortalEvent';
import PortalLayoutEditor from './PortalLayoutEditor';
import { normalizeLayout } from '../../../utils/portalLayout';
import { usePortalCanvasStyles, useDisplayFonts } from './PortalCanvasStyles';

/**
 * A Portal card's body — the one drawing of a Portal that the viewer, its page, the composer
 * and the page's editor all share. Handoff: Portal composer v3 (Sept 2026).
 *
 * The picture (or, for a text Portal, the animated background) fills the card, turned by the
 * layout's media rotation. The words sit on it at their saved position, in their saved font,
 * size, highlight and rotation; the event and the venue sit at their own, in their saved
 * styles, turned and scaled — the venue only when `venue` is given, which is the caller's rule
 * about who may see it. Positions are a percent of the card and the size is stored for a 400px
 * card, so the same layout lands the same on a phone and a desktop.
 *
 * With `editor` the static blocks are replaced by the interactive layer (drag, pinch, twist,
 * type, style, background). `mediaNode` lets the composer play its own local file.
 */
export default function PortalCanvas({
  media = null,             // StoryItemDto (kind video|image) or null for a text Portal
  mediaNode = undefined,    // the composer's own <video>/<img> layer, instead of StoryMedia
  text = '',
  chip = '',
  layout: rawLayout = null,
  venue = null,             // { name, address, city } — only for people who may see it
  venueDate = null,         // { mon, day, time } — the Portal's window, for the venue's DATE style
  event = null,             // the linked event row, or null
  eventLinkTo = null,       // where the event element goes in the viewer (null = nowhere)
  editor = null,            // PortalLayoutEditor props, minus layout/scale/text/event/venue
  muted = true,
  active = true,
  paused = false,
  loop = false,
  frame = undefined,        // a draft frame over the media's own
  onEnded = null,
  onError = null,
  onDuration = null,
  scrim = true,             // the 150px top scrim under the header
  className = '',
  style,
  children,
}) {
  usePortalCanvasStyles();
  const layout = useMemo(() => normalizeLayout(rawLayout), [rawLayout]);
  useDisplayFonts(layout, { all: !!editor, hasEvent: !!event, hasVenue: !!venue });

  const boxRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [offscreen, setOffscreen] = useState(false);

  // The size is for a 400px card; here the card is whatever it is.
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const measure = () => { const w = el.getBoundingClientRect().width; if (w) setScale(w / 400); };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // A card nobody can see stops moving.
  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([e]) => setOffscreen(!e.isIntersecting), { threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const hasMedia = mediaNode !== undefined ? true : !!media;
  const item = media ? { ...media, frame: frame !== undefined ? frame : media.frame } : null;
  // The caller may place the card (absolute inset-0); only default to relative when it hasn't.
  const positioned = /\b(absolute|fixed|sticky)\b/.test(className);
  const mediaRot = layout.rot.media || 0;

  const picture = mediaNode !== undefined ? mediaNode : item ? (
    <StoryMedia
      item={item}
      fit="cover"
      active={active}
      muted={muted}
      paused={paused}
      loop={loop}
      onEnded={onEnded}
      onError={onError}
      onDuration={onDuration}
    />
  ) : null;

  return (
    <div
      ref={boxRef}
      className={`prc-card overflow-hidden ${positioned ? '' : 'relative'} ${className}`}
      data-paused={paused || offscreen ? 'true' : 'false'}
      data-testid="portal-canvas"
      style={{ background: '#000', ...style }}
    >
      {hasMedia ? (
        <div
          data-testid="portal-media-layer"
          className="absolute inset-0"
          style={{ transform: mediaRot ? `rotate(${mediaRot}deg)` : undefined, transformOrigin: '50% 50%' }}
        >
          {picture}
        </div>
      ) : (
        <PortalBackground kind={layout.background} />
      )}

      {scrim && (
        <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0" style={{ height: 150, background: 'linear-gradient(to bottom, rgba(0,0,0,.75), rgba(0,0,0,0))', zIndex: 2 }} />
      )}

      {editor ? (
        <PortalLayoutEditor
          text={text}
          chip={chip}
          venue={venue}
          venueDate={venueDate}
          event={event}
          layout={layout}
          scale={scale}
          textOnly={editor.textOnly ?? !hasMedia}
          {...editor}
        />
      ) : (
        <>
          <PortalWords text={text} chip={chip} layout={layout} scale={scale} />
          {event && <PortalEvent event={event} layout={layout} linkTo={eventLinkTo} />}
          {venue && <PortalVenue venue={venue} layout={layout} date={venueDate} />}
        </>
      )}

      {children}
    </div>
  );
}
